define((require) => {
    "use strict";

    const Class           = require("shared/util/Class");
    const HttpStatusCodes = require("shared/util/HttpStatusCodes");
    const _               = require("underscore");

    // Standard error prefix for all errors (unless overridden)...
    const PREFIX = "The %{method} of %{name} failed because ";

    // Commonly known error codes. See also <tt>ErrorAction</tt>.
    const CODES = {
        BAD_REQUEST: "badrequest",
        UNSUPPORTED: "unsupported",
        UPLOAD:      "upload",      // same as "unsupported", but for Images specifically
        FORBIDDEN:   "forbidden",
        NOT_FOUND:   "notfound",
        TIMEOUT:     "timeout",
        VERSIONING:  "versioning",
        INTERNAL:    "internal",
        UNKNOWN:     "unknown",
        UNHANDLED:   "unhandled"
    };

    // Actions the user can try and take in case of errors. No action name is a subset of another! See also <tt>ErrorAction</tt>.
    const ACTIONS = {
        RELOGIN:   "relogin",
        RELOAD:    "reload",
        RETRY:     "retry",
        DUPLICATE: "duplicate",
        COMMUNITY: "community",
        SUPPORT:   "support",
        REMOVE_INCOMPATIBLE_WIDGETS: "removeIncompatibleWidgets"
    };

    /**
     * An <i>error handler</i> is used to translate actual errors into (more) user-friendly error-messages, such
     * as a http status code into a message, and to supply failure handlers to notify such errors to the users. <p>
     *
     * For now, only used by the editor, including channels admin app, but not by the viewer (because it has a lot
     * of editor specific code in it, should be refactored/re-thought)! <p>
     *
     * An error handler does not report any errors via the frontend error service, nor log them to the console
     * by default, but use configured callbacks to notify and discard the error. This is because the error handler
     * is used both in the viewer and the editor. In the editor, for example, the <tt>NotificationBar</tt> is
     * used to notify the user about errors. The app in question will ensure that the proper mechanisms for
     * notifying and discarding errors will have been set before actually used during app initialisation. <p>
     *
     * Error messages deduced by this error handler all have this format:
     * <pre>
     *   {
     *      message: String,        // The deduced error message to present to the user
     *      prefix:  String,        // The prefix of the deduced error message to present to the user, i.e. the generic
     *                              // part of each error message that will not vary for different causes, but for
     *                              // perhaps different model entities.
     *      model:   ImprovedModel, // The failing improved model, where relevant. Will fx be undefined on global
     *                              // unhandled errors, but set for sync failures.
     *      action:  String,        // The suggestion action(s) to take for the context alerting the error separated by
     *                              // space: "support", "community", "reload", "relogin", "duplicate", or "retry", or
     *                              // for example "reload duplicate". See <tt>ErrorHandler.Actions</tt>.
     *      code:    String         // A (group) code to link to the community, can encompass more (http) status codes.
     *                              // See <tt>ErrorHandler.Codes</tt>.
     *      status:  *              // The cause of the error, e.g. an <tt>Error</tt> or a http request status code.
     *   }
     * <pre>
     *
     * The <tt>error.message</tt> and <tt>error.prefix</tt> properties support simple value placeholders, where
     * actual values can be injected via <tt>ErrorHandler.inject(..)</tt>. This is useful to for example qualify
     * generic messages, as for example those generated by <tt>ErrorHandler.fromHttp(..)</tt>, with information
     * about the specific type of failing request (%{method}), the user-friendly name of the failing entity
     * (%{name}), the user-friendly type name of the entity (%{typeName}), and the cause (%{name}). See
     * <tt>ErrorHandler.inject(..)</tt> for further details.
     *
     * @class ErrorHandler
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     */
    const ErrorHandler = Class.extend({

        /**
         * @type {{show: function(error:*, options:*):String, discard: function(..)}
         *
         * The callbacks used to notify and possible discard notified error messages. <p>
         *
         * The <tt>show</tt> function will take the error object as the first argument and notification
         * objects as the second, returning the logical id of the notification so it eventually can
         * be discarded later on. The error message (and prefix) will have their value placeholders
         * resolved before passed to <tt>show</tt>. <p>
         *
         * The <tt>discard</tt> function takes said any arguments, e.g. a notification id, and will discard
         * the error, if not already and where applicable.
         */
        notify: {},

        /**
         * Alerts a given notification error based on the supplied error, unless the supplied function handler, if
         * any, returns a falsey value ("suppress error"). If supplied, its return values must be "notify", "alert", or
         * some falsey value to suppress the error. <p>
         *
         * In the editor, for example, on "notify" or any other truthy value different from "alert", the error will be
         * notified via <tt>NotificationBar</tt>, potentially queued. On "alert", the error will be shown immediately
         * as the current notification in the <tt>NotificationBar</tt> <b>and</b> optionally alerted via
         * <tt>windows.alert</tt> if no pending windows alert is currently shown and if <tt>windows.alerts</tt> are
         * configured to be used. Any falsey value will suppress the notification from this handler (but can be
         * performed via <tt>handler</tt>, of course). <p>
         *
         * In the viewer, the <tt>NotificationBar</tt> is not available, and the error will generally just be
         * logged to the console if logging is enabled. <p>
         *
         * Instead of a handler, a set of values can be supplied to be injected into the supplied error message
         * via <tt>ErrorHandler.inject(..)</tt>. If a function handler is supplied, it must by itself inject
         * relevant values into the placeholders. <p>
         *
         * Any error alerted via this function can be discarded again via <tt>discard(..)</tt>.
         *
         * @param {*|Number} error The error, as <tt>Error</tt>, a status code, or an error as defined by this error handler,
         *                         i.e. {message:String, action:String, model:ImprovedModel, status:*}. The error messages
         *                         can support at least the value placeholders %{method}, %{name}, %{typeName}, and %{status},
         *                         to have values injected via <tt>ErrorHandler.inject()</tt>. If undefined, an
         *                         error like {message: "unknown"} is used here.
         *
         * @param {Function|*} [handlerOrValues] The handler to potentially suppress or augment the error or a set
         *                                       of values to inject into error message placeholders; can be null. If
         *                                       a function, must return "alert", "notify", or some falsey value to
         *                                       suppress the error. If values, the error will not be suppressed.
         *
         * @return {String} The id of the notified error, if any; can be falsey if no error is notified.
         */
        fail(error, handlerOrValues) {
            let kind = "alert";
            const options = {};
            const handler = _.isFunction(handlerOrValues) && handlerOrValues;

            if (_.isNumber(error)) {
                error = this.fromHttp(error);
            } else if (typeof error === "undefined") {
                error = {message: "unknown"};
            }
            // Let handler process error, perhaps augmenting the error message and injecting values,
            // or suppressing it altogether...
            if (handler) {
                try {
                    kind = handler(error, options); // "alert", "notify", or falsey

                // Make sure error processing does not raise a spawned error...
                } catch (e) {
                    error.handlerError = e; // for tracing/reporting
                    // Fall-through
                }
            }
            options.alert = kind === "alert";
            options.queue = !options.alert;

            // Notify the user about the error?
            if (kind && this.notify.show) {
                // Resolve unless the handler was a function...
                if (!handler) {
                    this.inject(error, handlerOrValues);
                } else if (error.model || error.collection) {
                    this.inject(error);
                }
                return this.notify.show(error, options); // e.g. NotificationBar.error(error, options);
            }
        },

        /**
         * Discards the error notified/raised via <tt>ErrorHandler.fail(..)</tt>, if not already discarded.
         *
         * @param {*} [args] Arguments forwarded to <tt>notify.discard</tt> with this error handler as the
         *                   scope, if any.
         */
        discard(...args) {
            if (this.notify.discard) {
                this.notify.discard(...args); // e.g. NotificationBar.close({id: id, clear: "self"});
            }
        },

        /**
         * Adds a <tt>fail</tt> handler to the supplied http request promise. <p>
         *
         * On http error, an error will be deduced by <tt>fromHttp</tt> and if <tt>handlerOrValues</tt> is a function
         * like <tt>handler(error:*, options:*):String</tt>, it will be called with said error. The return value of
         * the handler, if any, can be "notify", "alert", or some falsey value. On "notify" or any other truthy value
         * different from "alert", the error will be notified via <tt>NotificationBar</tt>, potentially queued. On
         * "alert", the error will be shown immediately as the current notification in the <tt>NotificationBar</tt>
         * <b>and</b> optionally alerted via <tt>windows.alert</tt> if no pending windows alert is currently shown.
         * Any falsey value will suppress the notification from this handler (but can be performed via the handler,
         * of course). If a function handler is used, it must ensure that <tt>ErrorHandler.inject(..)</tt> is called
         * to replace value places in the error with actual values. <p>
         *
         * If <tt>handlerOrValues</tt> is an object storing values, they will be injected into the error on failure.
         *
         * @param {Promise}           promise    The promise for the http request; can be falsey, in which case this
         *                                       function does nothing.
         * @param {Function|*} [handlerOrValues] The handler to potentially suppress or augment the error or a set
         *                                       of values to inject into error message placeholders; can be null. If
         *                                       a function, must return "alert", "notify", or some falsey value to
         *                                       suppress the error. If values, the error will not be suppressed.
         *
         * @return {Promise} A promise with a proper http fail handler; will be falsey if <tt>promise</tt> is falsey.
         *                   The promise will on fail resolve to the id of the notification made, or falsey otherwise.
         */
        failHttp(promise, handlerOrValues) {
            return promise && promise.fail((xhr) => {
                if (xhr !== "cancelled") { // = Syncer.CANCELLED, but cannot be included because of circular dependencies!
                    return this.fail(this.fromHttp(xhr), handlerOrValues);
                }
            });
        },

        /**
         * Returns an error message from the supplied failing http request. <p>
         *
         * The error message will consists of a prefix text like "The %{method} of %{name} failed because ", and
         * a message with the detailed error post fixed, like prefix + "somebody else is modifying the %{typeName}."
         * on 409 - CONFLICT. For the error message to be useful, it must be resolved when values are ready for
         * injection into the placeholder via <tt>ErrorHandler.inject(..)</tt>.
         *
         * @param {*|Number} xhrOrStatus An already formatted error, the failing http(s) request or the failing status;
         *                               can be falsey which will default to 501 - Not Implemented. If an already
         *                               formatted error, it will simply be returned.
         * @param {*}       [values]     The values to inject into the described placeholders, if already available;
         *                               can be undefined.
         *
         * @return {{message:String, action:String, status:*}} An error message with the described value
         *         placeholders; never null.
         */
        fromHttp(xhrOrStatus, values) {
            // Make sure we do not get null pointers for deferred manually rejected, like from the font loader when
            // linked fonts are not supported (in Firefox for example), even though we actually expected the
            // failing xhr request here...
            xhrOrStatus = xhrOrStatus || {status: HttpStatusCodes.NOT_IMPLEMENTED}; // 501 - Not implemented in lack of something better, not used for anything else

            // Assumed already formatted if it has a "message" property (also for Error's)...
            if (xhrOrStatus.message) {
                return xhrOrStatus;
            }

            // TODO xhrOrStatus can also be a string. For instance it will be "timeout" if the browser times out the request.
            const status = typeof xhrOrStatus === "number" ? xhrOrStatus : xhrOrStatus.status;
            const error = {
                prefix: PREFIX, message: null, action: [ACTIONS.SUPPORT], code: null, status
            }; // set cause to status code to avoid keeping references to stale requests

            // Was retry!
            switch (status) {
                case HttpStatusCodes.BAD_REQUEST: // i.e. frontend error
                    error.message = "of invalid submitted data."; // does not make sense to retry, data is bad...
                    error.code    = CODES.BAD_REQUEST;
                    break;

                case HttpStatusCodes.UNAUTHORIZED:
                case HttpStatusCodes.FORBIDDEN:
                    error.message = "you are not authorized to perform the action for the %{typeName}.";
                    error.code    = CODES.FORBIDDEN;
                    error.action.push(ACTIONS.RELOGIN);
                    break;

                case HttpStatusCodes.NOT_FOUND:
                case HttpStatusCodes.METHOD_NOT_ALLOWED: // e.g. no such endpoint for the http method, raised via RESTEasy
                    error.message = "no such %{typeName} could be found."; // nothing to retry, i.e. not found!
                    error.code    = CODES.NOT_FOUND;
                    break;

                case HttpStatusCodes.REQUEST_TIMEOUT:
                    error.message = "of timeout.";
                    error.action.push(ACTIONS.RETRY);
                    error.code    = CODES.TIMEOUT;
                    break;

                case HttpStatusCodes.CONFLICT: // optimistic locking failed...
                    error.message = "somebody else is modifying the %{typeName}.";
                    error.action.push(ACTIONS.RELOAD, ACTIONS.DUPLICATE);  // duplicate only where applicable!
                    error.code    = CODES.VERSIONING;
                    break;

                case HttpStatusCodes.GONE:
                    error.message = "the %{typeName} has been deleted."; // nothing to retry, i.e. soft deleted!
                    error.code    = CODES.NOT_FOUND;
                    break;

                case HttpStatusCodes.REQUEST_ENTITY_TOO_LARGE: // request entity too large, i.e. user error
                case HttpStatusCodes.UNSUPPORTED_MEDIA_TYPE: // unsupported media type, i.e. user error
                case HttpStatusCodes.NOT_IMPLEMENTED: // not implemented (in lack of something better)
                    error.message = "the requested action is not supported."; // generally, a retry does not make sense, since it is like to be an image upload
                    error.code    = CODES.UNSUPPORTED;
                    break;

                case HttpStatusCodes.TOO_MANY_REQUESTS: // too many requests...
                    error.message = "too many requests have been submitted.";
                    break;

                case HttpStatusCodes.INTERNAL_SERVER_ERROR:  // Internal server error
                    error.message = "of an internal server error."; // A retry doesn't make sense because this is an edge case. The purging will fail only if Akamai is not available, never happened in Publicator
                    error.code    = CODES.INTERNAL;
                    break;

                // Success, which means the frontend caused the failure. This is typically due to a marshall
                // error on the backend, which returns a result (200), but with invalid json data that cannot
                // be parsed on the frontend, thus calling "fail"...
                case HttpStatusCodes.OK:
                    error.message = "of a potential %{typeName} data issue.";
                    error.action.push(ACTIONS.RETRY);  // no community error code
                    break;

                default: // some unknown type of server error...
                    error.message = "of a server error (cause %{status}).";
                    error.action.push(ACTIONS.RETRY);  // no community error code
                    error.code    = CODES.UNKNOWN;
                    break;
            }

            if (error.code) {
                error.action.push(ACTIONS.COMMUNITY);
            }

            // Do we have values to inject already?
            if (values) {
                this.inject(error, values);
            }

            error.action = error.action.join(" ");
            return error;
        },

        /**
         * Injects the specified values into the supplied error. <p>
         *
         * <tt>values</tt> are expected to store "method", "name", "typeName", and "status", but will default
         * to "request", "resource", "resource", and to <tt>error.status</tt> or "unknown", respectively. If the
         * values are a no-arg function, it will be called to return the value to inject. <p>
         *
         * If <tt>error.model</tt> is set, however, "method", "name", and "typeName" will default to
         * the current sync method, the distinguished name of the model, and the type name, respectively. <p>
         *
         * For example, the error prefix text can be something like "The %{method} of %{name} failed because ", and
         * the error message with the detailed error post fixed, like prefix + "somebody else is modifying the
         * %{typeName}." on 409 - CONFLICT. The placeholders %{method}, %{name}, and %{typeName} are expected to be
         * resolved to the logical sync request type, e.g. "save" or "destroy", %{name} to a user-friendly name of the
         * failing entity (model), and %{typeName} to logical type of resource is a user-friendly manner, e.g.
         * <tt>ImprovedModel.__typeName</tt>. In addition, %{status} can also be injected, i.e. the http response code
         * like 409.
         *
         * @param {{prefix:String, message:String, action:String, model:ImprovedModel, status:*}} error The error to
         *        inject resolved values into; cannot be null. If <tt>model</tt> is set, it will provide the default
         *        values of the real defaults.
         * @param {*} [values] The values to inject, indexed by their name. A key of "some_key" will be injected into
         *                     the error prefix/message the placeholder %{some_key} is found. Can be null, will default.
         *
         * @return {{prefix:String, message:String, action:String, status:*}} <tt>error</tt>, now with injected values.
         */
        inject(error, values) {
            let name;
            let value;
            let regex;

            const resource = error.model || error.collection;

            values = _.isObject(values) ? values : {};

            values.method   = values.method   || (resource && resource.syncState && resource.syncState.current.method) || "request";
            values.name     = values.name     || (resource && resource.distinguishedName())                            || "resource";
            values.typeName = values.typeName || (resource && resource.__typeName)                                     || "resource";
            values.status   = values.status   || error.status                                                          || "unknown";

            // A bit of a hack, but texts will sound odd otherwise...
            if (values.name === values.typeName) {
                values.name = `the ${values.name}`;
            }
            if (!error.prefix) {
                error.prefix = PREFIX;
            }
            // Try a final resolve if we do not have a resolved error message at this point. We have to extend
            // the incoming error, since a reference to the actual error...
            if (!error.message) {
                _.extend(error, this.fromHttp(values.status));
            }

            // Replace...
            for (name in values) {
                if (values.hasOwnProperty(name)) {
                    regex = new RegExp(`\\%\\{${name}\\}`, "gi");
                    value = values[name];
                    if (_.isFunction(value)) {
                        value = value();
                    }
                    error.prefix  = error.prefix.replace(regex, value);
                    error.message = error.message.replace(regex, value);
                }
            }

            // To handle multiple inject calls...
            if (error.message.indexOf(error.prefix) === -1) {
                error.message = error.prefix + error.message;
            }

            return error;
        }

    });

    // Singleton, so we have to add static variables directly...
    const handler = new ErrorHandler();

    handler.PREFIX  = PREFIX;
    handler.Codes   = CODES;
    handler.Actions = ACTIONS;

    return handler;
});



// WEBPACK FOOTER //
// ./src/shared/services/error/ErrorHandler.js